/**
 * 路由级错误边界 — 包裹单个路由的 element，
 * 仅当前页面崩溃时显示错误 UI，不影响其他路由或全局导航。
 *
 * 与全局 ErrorBoundary 的区别：
 * - 全局 ErrorBoundary 兜底整个应用（白屏防护）
 * - RouteErrorBoundary 只兜底单个页面，AppShell 导航保持可用
 *
 * 错误 UI：「页面出错」+ 重试按钮（reload）+ 返回书架链接
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Hump] RouteErrorBoundary caught:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 p-6">
          <p className="text-6xl">🐪</p>
          <div className="text-center">
            <h1 className="text-xl font-serif text-camel-dark mb-2">页面出错</h1>
            <p className="text-sm text-charcoal/60 dark:text-charcoal-light/60 max-w-md">
              当前页面遇到了意外错误。其他页面不受影响，你的数据不会丢失（存储在本地）。
            </p>
          </div>
          {this.state.error && (
            <details className="max-w-lg w-full">
              <summary className="text-xs text-charcoal/40 dark:text-charcoal-light/40 cursor-pointer">
                技术详情
              </summary>
              <pre className="mt-2 p-3 rounded-md bg-warm-white dark:bg-border-warm-dark text-xs text-error overflow-x-auto whitespace-pre-wrap">
                {this.state.error.message}
                {this.state.error.stack?.slice(0, 500)}
              </pre>
            </details>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={this.handleReload}
              className="px-6 py-2.5 rounded-md bg-camel text-white font-medium hover:bg-camel-light transition-colors"
            >
              重试
            </button>
            <a
              href="#/shelf"
              className="px-6 py-2.5 rounded-md border border-border-warm dark:border-border-warm-dark text-charcoal dark:text-charcoal-light font-medium hover:bg-warm-white dark:hover:bg-border-warm-dark transition-colors"
            >
              返回书架
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
